import * as React from 'react'
import { Logger } from 'src/3rds/cube'

import { makeStyles } from 'tss-react/mui'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import LinearProgress from '@mui/material/LinearProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'

import { formatarDia } from '../Dashboards.periodo'
import { DashboardsScope, Negocios, PorFase, RESERVAS_POR_PAGINA } from '../Dashboards.scopes'
import {
    COR_FASE,
    FASES,
    opcaoCriados,
    opcaoGatilhos,
    opcaoMovimento,
    opcaoPipelines,
    percentual,
    principaisPipelines,
    ROTULO_FASE
} from '../Negocios.graficos'
import { Grafico, Numero, useCardStyles } from './Componentes'
import { EChart } from './EChart'

const LOG = Logger.get('Negocios-VIEW')

const numero = (n: number) => n.toLocaleString('pt-BR')

const useStyles = makeStyles()((theme) => ({
    blocos: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: theme.spacing(2.5)
    },
    numeros: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: theme.spacing(2)
    },
    lado: {
        display: 'flex',
        gap: theme.spacing(2.5),
        flexWrap: 'wrap',
        '& > *': {
            flex: '1 1 420px',
            minWidth: 0
        }
    },
    cabecalhoTabela: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap'
    },
    marca: {
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: 3,
        marginRight: 6,
        verticalAlign: 'baseline'
    }
}))

/** Cabeçalho de coluna com a cor da fase, para ligar a tabela ao gráfico. */
function CabecalhoFase({ fase }: { fase: keyof PorFase }) {
    const { classes } = useStyles()
    return (
        <TableCell align="right">
            <span className={classes.marca} style={{ backgroundColor: COR_FASE[fase] }} />
            {ROTULO_FASE[fase]}
        </TableCell>
    )
}

/** Tabela por fase (pipelines ou esteiras), com o total de cada linha. */
function TabelaPorFase({ titulo, linhas }: { titulo: string; linhas: (PorFase & { nome: string })[] }) {
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{titulo}</TableCell>
                        {FASES.map((fase) => (
                            <CabecalhoFase key={fase} fase={fase} />
                        ))}
                        <TableCell align="right">Total</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {linhas.map((l) => (
                        <TableRow key={l.nome}>
                            <TableCell>{l.nome}</TableCell>
                            {FASES.map((fase) => (
                                <TableCell key={fase} align="right">
                                    {numero(l[fase])}
                                </TableCell>
                            ))}
                            <TableCell align="right">{numero(l.A + l.R + l.K + l.C)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

function TabelaMovimento({ movimento }: { movimento: Negocios['movimento'] }) {
    return (
        <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Dia</TableCell>
                        <TableCell align="right">Abordagem</TableCell>
                        <TableCell align="right">Reserva ou Cota</TableCell>
                        <TableCell align="right">Derrubadas</TableCell>
                        <TableCell align="right">Cliente</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {movimento.map((m) => (
                        <TableRow key={m.dia}>
                            <TableCell>{formatarDia(m.dia, true)}</TableCell>
                            <TableCell align="right">{numero(m.abordagens)}</TableCell>
                            <TableCell align="right">{numero(m.reservas)}</TableCell>
                            <TableCell align="right">{numero(m.derrubadas)}</TableCell>
                            <TableCell align="right">{numero(m.clientes)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

/** Dashboard de negócios e disputas (E17): como está a carteira e quem disputa o quê? */
export function NegociosPainel({ scope }: { scope: DashboardsScope }) {
    const { classes } = useStyles()
    const { classes: cards } = useCardStyles()
    const negocios = scope.negocios!
    const periodo = negocios.periodo

    const opcoes = React.useMemo(
        () => ({
            pipelines: opcaoPipelines(negocios.pipelines),
            gatilhos: opcaoGatilhos(negocios.gatilhos),
            movimento: opcaoMovimento(negocios.movimento, periodo),
            criados: opcaoCriados(negocios.criados)
        }),
        [negocios, periodo]
    )

    const { carteira, prazos, derrubadas, criados } = negocios
    const semHistoricoAntes = negocios.historicoDesde == null || negocios.historicoDesde > periodo.de
    const avancaram = criados.R + criados.K + criados.C

    const emitVencendoDias = (_evt: unknown, dias: 7 | 15 | null) => {
        if (dias) scope.onVencendoDiasChange(dias).catch(LOG.caught)
    }
    const emitVencendoPagina = (_evt: unknown, pagina: number) => scope.onVencendoPaginaChange(pagina).catch(LOG.caught)

    return (
        <Box className={classes.blocos}>
            <Box className={classes.numeros}>
                <Numero
                    titulo="Em Abordagem"
                    valor={numero(carteira.A)}
                    nota="Negócios em andamento, antes da reserva"
                />
                <Numero
                    titulo="Em Reserva ou Cota"
                    valor={numero(carteira.R + carteira.K)}
                    nota={`${numero(carteira.R)} em Reserva e ${numero(carteira.K)} em Cota`}
                />
                <Numero titulo="Clientes" valor={numero(carteira.C)} nota="Negócios em etapa de cliente" />
                <Numero
                    titulo="Reservas que vencem em 15 dias"
                    valor={numero(prazos.vencem15)}
                    nota={`${numero(prazos.vencem7)} em 7 dias · ${numero(prazos.vencido)} com prazo já vencido`}
                />
            </Box>

            <Grafico
                titulo="Carteira por pipeline"
                nota="Negócios em andamento hoje, fora os pipelines que não entram nas decisões da LIA."
                tabela={
                    <TabelaPorFase titulo="Pipeline" linhas={negocios.pipelines.map((p) => ({ ...p, nome: p.nome }))} />
                }
            >
                <EChart
                    option={opcoes.pipelines}
                    height={Math.max(220, principaisPipelines(negocios.pipelines).length * 34 + 60)}
                    ariaLabel="Carteira por pipeline, empilhada por fase"
                />
            </Grafico>

            <Box className={classes.lado}>
                <Card className={cards.card}>
                    <CardContent>
                        <Typography className={cards.tituloCard}>Carteira por esteira</Typography>
                        <Typography className={cards.nota}>
                            A esteira vem da equipe de vendas (scale_int___selecttype). “Outro rótulo” são os nomes
                            antigos, como Alta e Baixa.
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <TabelaPorFase
                                titulo="Esteira"
                                linhas={negocios.esteiras.map((e) => ({ ...e, nome: e.esteira }))}
                            />
                        </Box>
                    </CardContent>
                </Card>

                <Grafico titulo="Reservas e Cotas por gatilho" nota="O que garantiu cada reserva em andamento hoje.">
                    <EChart
                        option={opcoes.gatilhos}
                        height={Math.max(200, Math.min(negocios.gatilhos.length, 8) * 32 + 40)}
                        ariaLabel="Reservas e Cotas por gatilho"
                    />
                </Grafico>
            </Box>

            <Box className={classes.lado}>
                <Grafico
                    titulo="Movimento por dia"
                    nota={
                        semHistoricoAntes && negocios.historicoDesde
                            ? `O histórico de fase começa em ${formatarDia(negocios.historicoDesde, true)}.`
                            : 'Entradas em cada fase e negócios derrubados por outro parceiro.'
                    }
                    tabela={<TabelaMovimento movimento={negocios.movimento} />}
                >
                    {negocios.historicoDesde ? (
                        <EChart
                            option={opcoes.movimento}
                            ariaLabel="Movimento por dia: entradas em abordagem, reserva, cliente e derrubadas"
                        />
                    ) : (
                        <Alert severity="info">O histórico de fase ainda não tem registros.</Alert>
                    )}
                </Grafico>

                <Grafico
                    titulo="Para onde foram os negócios criados no período"
                    nota={`${numero(criados.total)} criados de ${formatarDia(periodo.de, true)} a ${formatarDia(
                        periodo.ate,
                        true
                    )}; ${percentual(avancaram, criados.total)} já estão em Reserva, Cota ou Cliente.`}
                >
                    <EChart
                        option={opcoes.criados}
                        height={220}
                        ariaLabel="Negócios criados no período, por fase de hoje"
                    />
                </Grafico>
            </Box>

            <Numero
                titulo="Derrubados por outro parceiro no período"
                valor={numero(derrubadas.total)}
                nota={
                    derrubadas.total === 0
                        ? 'Nenhuma derrubada no período.'
                        : `${numero(derrubadas.porReservaDocumento)} por reserva com documento, ${numero(
                              derrubadas.porReservaEtapa
                          )} por reserva por etapa, ${numero(derrubadas.porCota)} por Cota e ${numero(
                              derrubadas.porCliente
                          )} porque o grupo virou cliente.`
                }
            />

            <Card className={cards.card}>
                {scope.carregandoVencendo && <LinearProgress />}
                <CardContent>
                    <Box className={classes.cabecalhoTabela}>
                        <Box>
                            <Typography className={cards.tituloCard}>Reservas que vencem</Typography>
                            <Typography className={cards.nota}>
                                Reservas e Cotas com prazo nos próximos dias, da que vence antes para a que vence
                                depois.
                            </Typography>
                        </Box>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={scope.vencendoDias}
                            onChange={emitVencendoDias}
                            aria-label="Prazo"
                        >
                            <ToggleButton value={7} sx={{ textTransform: 'none' }}>
                                7 dias
                            </ToggleButton>
                            <ToggleButton value={15} sx={{ textTransform: 'none' }}>
                                15 dias
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <TableContainer sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Vence em</TableCell>
                                    <TableCell>Negócio</TableCell>
                                    <TableCell>Parceiro</TableCell>
                                    <TableCell>Pipeline</TableCell>
                                    <TableCell>Fase</TableCell>
                                    <TableCell>Gatilho</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scope.vencendo.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>Nenhuma reserva vence nesse prazo.</TableCell>
                                    </TableRow>
                                )}
                                {scope.vencendo.map((r) => (
                                    <TableRow key={r.dealId}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {formatarDia(r.venceEm, true)}
                                        </TableCell>
                                        <TableCell>{r.negocio}</TableCell>
                                        <TableCell>{r.parceiro ?? '—'}</TableCell>
                                        <TableCell>{r.pipeline}</TableCell>
                                        <TableCell>{ROTULO_FASE[r.fase]}</TableCell>
                                        <TableCell>{r.gatilho ?? '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={scope.vencendoTotal}
                        page={scope.vencendoPagina}
                        onPageChange={emitVencendoPagina}
                        rowsPerPage={RESERVAS_POR_PAGINA}
                        rowsPerPageOptions={[RESERVAS_POR_PAGINA]}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${numero(count)}`}
                    />
                </CardContent>
            </Card>
        </Box>
    )
}

export default NegociosPainel
